import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearUser } from '../../features/auth/authSlice';
import { loginUser, fetchMe } from '../../services/userApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { saveAuthData, clearAuthData } from '../../utils/authStorage';
import { extractAccessToken } from '../../utils/tokenHelpers';
import { normalizeStaffUser } from '../../utils/staffUser';
import { formatApiError } from '../../utils/laravelError';
import { adminSession } from '../../utils/session';
import './Login.css';


function Login() {
  const dispatch = useDispatch();
  const { login, logout, isAuthenticated, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { roles } = useSelector(state => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (roles?.includes('admin') || roles?.includes('shisha_master')) {
      navigate('/admin/hookah-flavors', { replace: true });
    } else if (roles?.includes('waiter')) {
      navigate('/admin/scan-qr', { replace: true });
    }
  }, [isLoading, isAuthenticated, roles, navigate]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setError('');
    setLoading(true);
    let sessionStarted = false;
    try {
      const data = await loginUser({ email, password });
      const token = extractAccessToken(data);
      if (!token) {
        throw new Error('Login failed: no token received from server.');
      }
      // In-memory only — httpOnly cookie handles refresh / new tabs
      adminSession.setBearerToken(token);

      login({ expiresIn: data.expires_in });
      sessionStarted = true;

      const me = await fetchMe();
      const userData = normalizeStaffUser(me);
      const bearerToken = adminSession.getBearerToken();

      dispatch(setUser({ user: userData, token: bearerToken }));
      saveAuthData(userData, {
        expiresIn: data.expires_in,
        expiresAt: me.session?.expires_at,
      });

      if (userData.roles.includes('admin') || userData.roles.includes('shisha_master')) {
        navigate('/admin/hookah-flavors', { replace: true });
      } else if (userData.roles.includes('waiter')) {
        navigate('/admin/scan-qr', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      if (sessionStarted) {
        logout();
        clearAuthData();
        dispatch(clearUser());
      }
      setError(formatApiError(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleSubmit}>
        <h2>Login</h2>
        {error && <div className="login-error">{error}</div>}
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <label htmlFor="password">Password</label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
      </form>
    </div>
  );
}

export default Login;
